import React, { useState, useEffect } from 'react';
import {
    Business,
    Store,
    LocationOn,
    Language,
    Email,
    Phone,
    Public,
    ArrowBack,
    Description,
    ExpandMore,
    Category,
    CheckCircle
} from '@mui/icons-material';
import { createOrganization, addOrganizationContact, fetchOrganizationCategories } from '../../services/organizationService';
import { storageKeys } from '../../config/environment';

const OrganizationCreation = ({ onOrganizationCreated, onBack }) => {
    const [formData, setFormData] = useState({
        // Informations de base
        name: '',
        category: '',
        category_id: '',
        description: '',
        tags: '',

        // Contact
        phone: '',
        email: '',
        website: '',

        // Localisation
        country: '',
        province: '',
        city: '',
        avenue: '',
        quartier: '',
        numero: ''
    })

    const [currentStep, setCurrentStep] = useState(1)
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [apiCategories, setApiCategories] = useState([])
    const [createdOrgId, setCreatedOrgId] = useState(null)

    // Charger les catégories depuis l'API
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const solutionId = localStorage.getItem(storageKeys.solution);
                if (solutionId) {
                    const categories = await fetchOrganizationCategories(solutionId);
                    setApiCategories(categories);
                }
            } catch (error) {
                console.error('Erreur lors du chargement des catégories:', error);
            }
        };
        loadCategories();
    }, [])

    const categories = [
        { value: 'retail', label: 'Commerce de détail', icon: '🏪', description: 'Boutique, supermarché, magasin' },
        { value: 'restaurant', label: 'Restauration', icon: '🍽️', description: 'Restaurant, café, fast-food' },
        { value: 'service', label: 'Service', icon: '🔧', description: 'Réparation, consultation, service professionnel' },
        { value: 'ecommerce', label: 'E-commerce', icon: '🛒', description: 'Vente en ligne, marketplace' },
        { value: 'health', label: 'Santé', icon: '🏥', description: 'Pharmacie, clinique, cabinet médical' },
        { value: 'education', label: 'Éducation', icon: '🎓', description: 'École, formation, institut' },
        { value: 'beauty', label: 'Beauté', icon: '💅', description: 'Salon de coiffure, esthétique, spa' },
        { value: 'automotive', label: 'Automobile', icon: '🚗', description: 'Garage, concessionnaire, pièces auto' },
        { value: 'construction', label: 'Construction', icon: '🏗️', description: 'BTP, matériaux, immobilier' },
        { value: 'technology', label: 'Technologie', icon: '💻', description: 'Informatique, électronique, digital' },
        { value: 'other', label: 'Autre', icon: '🏢', description: 'Autre type d\'activité' }
    ]

    const countries = [
        'RD Congo',
        'Autre'
    ]

    const provinces = [
        'Kinshasa',
        'Kongo-Central',
        'Kwilu',
        'Kwango',
        'Mai-Ndombe',
        'Équateur',
        'Mongala',
        'Nord-Ubangi',
        'Sud-Ubangi',
        'Tshuapa',
        'Tshopo',
        'Haut-Uélé',
        'Bas-Uélé',
        'Ituri',
        'Nord-Kivu',
        'Sud-Kivu',
        'Maniema',
        'Haut-Lomami',
        'Tanganyika',
        'Haut-Katanga',
        'Lualaba',
        'Kasaï',
        'Kasaï-Central',
        'Kasaï-Oriental',
        'Lomami',
        'Sankuru'
    ]

    const cities = [
        'Kinshasa',
        'Lubumbashi',
        'Mbuji-Mayi',
        'Kananga',
        'Kisangani',
        'Bukavu',
        'Goma',
        'Matadi',
        'Boma',
        'Kolwezi',
        'Likasi',
        'Bandundu',
        'Mbandaka',
        'Kindu',
        'Kikwit',
        'Butembo',
        'Beni',
        'Tshikapa'
    ]

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleCategorySelect = (category) => {
        // Si c'est une catégorie de l'API, utiliser category_id
        const apiCategory = apiCategories.find(cat => cat.id === category.value || cat.name === category.label);
        if (apiCategory) {
            setFormData(prev => ({ 
                ...prev, 
                category: category.value,
                category_id: apiCategory.id 
            }))
        } else {
            setFormData(prev => ({ ...prev, category: category.value }))
        }
        setShowCategoryDropdown(false)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)
        setError('')

        try {
            // Étape 1: Créer l'organisation (comme sur mobile)
            const organizationData = {
                name: formData.name,
                category_id: formData.category_id || formData.category,
                description: formData.description || '',
                tags: formData.tags || ''
            }

            const createdOrg = await createOrganization(organizationData)
            
            console.log('[OrganizationCreation] Réponse de createOrganization:', createdOrg);
            
            // Côté mobile, payload contient l'organisation avec son id (ligne 93 de AddOrgScreen.js)
            // La structure peut être : {id, name, ...} directement ou dans payload
            let orgId = null;
            
            if (createdOrg) {
                // Essayer différentes structures possibles
                orgId = createdOrg.id || 
                       createdOrg.payload?.id || 
                       createdOrg.contents?.id ||
                       (typeof createdOrg === 'object' && createdOrg.id);
            }

            if (!orgId) {
                console.error('[OrganizationCreation] Structure de réponse inattendue:', createdOrg);
                throw new Error('Organisation créée mais ID non retourné. Réponse reçue: ' + JSON.stringify(createdOrg))
            }

            console.log('[OrganizationCreation] ID de l\'organisation créée:', orgId);
            setCreatedOrgId(orgId)

            // Étape 2: Ajouter le contact (comme sur mobile)
            const contactData = {
                zip: '243',
                phone: formData.phone || '',
                email: formData.email || '',
                official_web_site: formData.website || ''
            }

            await addOrganizationContact(orgId, contactData)

            // Préparer l'organisation pour le callback
            const newOrganization = {
                id: orgId,
                name: formData.name,
                category: formData.category,
                description: formData.description,
                role: 'admin',
                logo: categories.find(cat => cat.value === formData.category)?.icon || '🏢',
                status: 'active',
                users: 1,
                createdAt: new Date().toISOString().split('T')[0],
                revenue: 0
            }

            onOrganizationCreated(newOrganization)
        } catch (error) {
            console.error('Erreur lors de la création de l\'organisation:', error)
            setError(error.message || 'Erreur lors de la création de l\'organisation')
        } finally {
            setLoading(false)
        }
    }

    const nextStep = () => {
        setCurrentStep(prev => Math.min(prev + 1, 3))
    }

    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1))
    }

    const isStepValid = (step) => {
        switch(step) {
            case 1:
                return formData.name && formData.category
            case 2:
                return formData.phone && formData.email
            case 3:
                return formData.country && formData.city
            default:
                return false
        }
    }

    const getSelectedCategory = () => {
        return categories.find(cat => cat.value === formData.category)
    }

    // Styles optimisés
    const inputStyles = "w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"

    const selectStyles = "w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 appearance-none pr-10"

    const labelStyles = "block text-sm font-semibold text-gray-700 mb-2"

    return (
        <div className="flex justify-center items-center min-h-[80vh] p-4 bg-gradient-to-br from-gray-50 via-amber-50 to-orange-50">
            <div className="w-full max-w-4xl">
                {/* Header avec animation */}
                <div className="text-center mb-8">
                    <button
                        onClick={onBack}
                        className="inline-flex items-center text-gray-600 hover:text-amber-600 mb-4 transition-all duration-200 group bg-white/80 backdrop-blur-sm rounded-2xl px-4 py-2 border border-gray-200 hover:border-amber-200 shadow-sm"
                    >
                        <ArrowBack className="mr-2 group-hover:-translate-x-1 transition-transform" />
                        Retour à la sélection
                    </button>
                    <div className="relative inline-block">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4 relative z-10">
                            Créer votre Organisation
                        </h1>
                        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-500 rounded-full"></div>
                    </div>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                        Configurez votre première organisation Flow POS en quelques étapes simples
                    </p>
                </div>

                {/* Progress Bar améliorée */}
                <div className="flex justify-center mb-8">
                    <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-gray-200 shadow-sm">
                        {[1, 2, 3].map((step) => (
                            <React.Fragment key={step}>
                                <div className={`flex flex-col items-center transition-all duration-300 ${
                                    currentStep >= step ? 'text-amber-600' : 'text-gray-400'
                                }`}>
                                    <div className={`w-14 h-14 rounded-2xl border-2 flex flex-col items-center justify-center transition-all duration-300 relative overflow-hidden ${
                                        currentStep >= step
                                            ? 'bg-gradient-to-br from-amber-500 to-amber-600 border-amber-500 text-white shadow-lg'
                                            : currentStep === step
                                                ? 'border-amber-300 bg-white text-amber-600 shadow-md'
                                                : 'border-gray-300 bg-white text-gray-400'
                                    }`}>
                                        <span className="text-sm font-bold">{step}</span>
                                        <span className="text-[10px] opacity-90">
                                            {step === 1 && 'Infos'}
                                            {step === 2 && 'Contact'}
                                            {step === 3 && 'Local'}
                                        </span>
                                        {currentStep > step && (
                                            <CheckCircle className="absolute top-1 right-1 w-3 h-3" />
                                        )}
                                    </div>
                                </div>
                                {step < 3 && (
                                    <div className={`w-12 h-1 rounded-full transition-all duration-300 ${
                                        currentStep > step ? 'bg-amber-500' : 'bg-gray-200'
                                    }`}></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Form Container */}
                <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-2xl">
                    {/* Message d'erreur */}
                    {error && (
                        <div className="mx-8 mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                            <p className="font-semibold">Erreur</p>
                            <p className="text-sm">{error}</p>
                        </div>
                    )}
                    <form onSubmit={handleSubmit}>
                        <div className="p-8">
                            {/* Step 1: Informations de base */}
                            {currentStep === 1 && (
                                <div className="space-y-6 animate-fadeIn">
                                    <div className="flex items-center mb-6 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl border border-amber-200 shadow-sm">
                                        <Business className="text-amber-600 mr-3 text-2xl" />
                                        <div>
                                            <h2 className="text-2xl font-bold text-gray-900">Informations de l'Organisation</h2>
                                            <p className="text-gray-600">Définissez l'identité de votre entreprise</p>
                                        </div>
                                    </div>

                                    {/* Nom de l'organisation */}
                                    <div className="group">
                                        <label className={labelStyles}>
                                            Nom de l'organisation *
                                        </label>
                                        <div className="relative">
                                            <Store className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-amber-500 transition-colors" />
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className={`${inputStyles} pl-12 transition-all duration-200`}
                                                placeholder="Ex: Ma Boutique Flow"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Catégorie - Liste déroulante améliorée */}
                                    <div className="group">
                                        <label className={labelStyles}>
                                            Catégorie d'activité *
                                        </label>
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                                                className={`w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-left transition-all duration-200 flex items-center justify-between group-hover:border-amber-300 ${
                                                    formData.category ? 'text-gray-900' : 'text-gray-500'
                                                } ${showCategoryDropdown ? 'ring-2 ring-amber-500 border-amber-500' : ''}`}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <Category className="text-gray-400" />
                                                    {formData.category ? (
                                                        <div className="flex items-center space-x-2">
                                                            <span className="text-lg">{getSelectedCategory()?.icon}</span>
                                                            <span>{getSelectedCategory()?.label}</span>
                                                        </div>
                                                    ) : (
                                                        <span>Sélectionnez une catégorie</span>
                                                    )}
                                                </div>
                                                <ExpandMore className={`text-gray-400 transition-transform duration-200 ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                                            </button>

                                            {/* Dropdown Menu */}
                                            {showCategoryDropdown && (
                                                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 max-h-80 overflow-y-auto">
                                                    <div className="p-2">
                                                        {/* Afficher d'abord les catégories de l'API si disponibles */}
                                                        {apiCategories.length > 0 ? (
                                                            apiCategories.map((category) => (
                                                                <button
                                                                    key={category.id}
                                                                    type="button"
                                                                    onClick={() => handleCategorySelect({ value: category.id, label: category.name })}
                                                                    className={`w-full p-3 rounded-lg text-left transition-all duration-200 flex items-center space-x-3 group ${
                                                                        formData.category_id === category.id
                                                                            ? 'bg-amber-50 border border-amber-200'
                                                                            : 'hover:bg-gray-50'
                                                                    }`}
                                                                >
                                                                    <span className="text-2xl transition-transform group-hover:scale-110">
                                                                        🏢
                                                                    </span>
                                                                    <div className="flex-1">
                                                                        <div className="font-medium text-gray-900">
                                                                            {category.name}
                                                                        </div>
                                                                    </div>
                                                                    {formData.category_id === category.id && (
                                                                        <CheckCircle className="text-amber-500 w-5 h-5" />
                                                                    )}
                                                                </button>
                                                            ))
                                                        ) : (
                                                            // Fallback sur les catégories locales
                                                            categories.map((category) => (
                                                                <button
                                                                    key={category.value}
                                                                    type="button"
                                                                    onClick={() => handleCategorySelect(category)}
                                                                    className={`w-full p-3 rounded-lg text-left transition-all duration-200 flex items-center space-x-3 group ${
                                                                        formData.category === category.value
                                                                            ? 'bg-amber-50 border border-amber-200'
                                                                            : 'hover:bg-gray-50'
                                                                    }`}
                                                                >
                                                                    <span className="text-2xl transition-transform group-hover:scale-110">
                                                                        {category.icon}
                                                                    </span>
                                                                    <div className="flex-1">
                                                                        <div className="font-medium text-gray-900">
                                                                            {category.label}
                                                                        </div>
                                                                        <div className="text-sm text-gray-500">
                                                                            {category.description}
                                                                        </div>
                                                                    </div>
                                                                    {formData.category === category.value && (
                                                                        <CheckCircle className="text-amber-500 w-5 h-5" />
                                                                    )}
                                                                </button>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className="group">
                                        <label className={labelStyles}>
                                            Description
                                            <span className="text-gray-500 font-normal ml-1">(optionnel)</span>
                                        </label>
                                        <div className="relative">
                                            <Description className="absolute left-3 top-3 text-gray-400 group-focus-within:text-amber-500 transition-colors" />
                                            <textarea
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                rows="3"
                                                className={`${inputStyles} pl-12 resize-none transition-all duration-200`}
                                                placeholder="Décrivez brièvement votre activité, vos produits ou services..."
                                            />
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                                            <span>Maximum 500 caractères</span>
                                            <span>{formData.description.length}/500</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Contact */}
                            {currentStep === 2 && (
                                <div className="space-y-6 animate-fadeIn">
                                    <div className="flex items-center mb-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl border border-blue-200 shadow-sm">
                                        <Phone className="text-blue-600 mr-3 text-2xl" />
                                        <div>
                                            <h2 className="text-2xl font-bold text-gray-900">Contact de l'Organisation</h2>
                                            <p className="text-gray-600">Comment vos clients peuvent vous contacter</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Téléphone */}
                                        <div className="group">
                                            <label className={labelStyles}>
                                                Numéro de contact *
                                            </label>
                                            <div className="relative">
                                                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    className={`${inputStyles} pl-12 transition-all duration-200`}
                                                    placeholder="+243 97 123 4567"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="group">
                                            <label className={labelStyles}>
                                                Email *
                                            </label>
                                            <div className="relative">
                                                <Email className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    className={`${inputStyles} pl-12 transition-all duration-200`}
                                                    placeholder="contact@organisation.com"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/* Site web */}
                                        <div className="md:col-span-2 group">
                                            <label className={labelStyles}>
                                                Site web officiel
                                                <span className="text-gray-500 font-normal ml-1">(optionnel)</span>
                                            </label>
                                            <div className="relative">
                                                <Language className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                                <input
                                                    type="url"
                                                    name="website"
                                                    value={formData.website}
                                                    onChange={handleInputChange}
                                                    className={`${inputStyles} pl-12 transition-all duration-200`}
                                                    placeholder="https://www.organisation.com"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Localisation */}
                            {currentStep === 3 && (
                                <div className="space-y-6 animate-fadeIn">
                                    <div className="flex items-center mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200 shadow-sm">
                                        <LocationOn className="text-green-600 mr-3 text-2xl" />
                                        <div>
                                            <h2 className="text-2xl font-bold text-gray-900">Localisation</h2>
                                            <p className="text-gray-600">Où se trouve votre établissement</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Pays */}
                                        <div className="group">
                                            <label className={labelStyles}>
                                                Pays *
                                            </label>
                                            <div className="relative">
                                                <Public className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors" />
                                                <select
                                                    name="country"
                                                    value={formData.country}
                                                    onChange={handleInputChange}
                                                    className={`${selectStyles} pl-12 transition-all duration-200`}
                                                    required
                                                >
                                                    <option value="" className="text-gray-500">Sélectionnez un pays</option>
                                                    {countries.map(country => (
                                                        <option key={country} value={country} className="text-gray-900">{country}</option>
                                                    ))}
                                                </select>
                                                <ExpandMore className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                                            </div>
                                        </div>

                                        {/* Province/Région */}
                                        <div className="group">
                                            <label className={labelStyles}>
                                                Province/Région
                                                <span className="text-gray-500 font-normal ml-1">(optionnel)</span>
                                            </label>
                                            <div className="relative">
                                                <select
                                                    name="province"
                                                    value={formData.province}
                                                    onChange={handleInputChange}
                                                    className={`${selectStyles} transition-all duration-200`}
                                                >
                                                    <option value="" className="text-gray-500">Sélectionnez une région</option>
                                                    {provinces.map(province => (
                                                        <option key={province} value={province} className="text-gray-900">{province}</option>
                                                    ))}
                                                </select>
                                                <ExpandMore className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                                            </div>
                                        </div>

                                        {/* Ville */}
                                        <div className="group">
                                            <label className={labelStyles}>
                                                Ville *
                                            </label>
                                            <div className="relative">
                                                <select
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    className={`${selectStyles} transition-all duration-200`}
                                                    required
                                                >
                                                    <option value="" className="text-gray-500">Sélectionnez une ville</option>
                                                    {cities.map(city => (
                                                        <option key={city} value={city} className="text-gray-900">{city}</option>
                                                    ))}
                                                </select>
                                                <ExpandMore className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                                            </div>
                                        </div>

                                        {/* Numéro */}
                                        <div className="group">
                                            <label className={labelStyles}>
                                                Numéro
                                                <span className="text-gray-500 font-normal ml-1">(optionnel)</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="numero"
                                                value={formData.numero}
                                                onChange={handleInputChange}
                                                className={`${inputStyles} transition-all duration-200`}
                                                placeholder="N° 123"
                                            />
                                        </div>

                                        {/* Avenue */}
                                        <div className="group">
                                            <label className={labelStyles}>
                                                Avenue/Rue
                                                <span className="text-gray-500 font-normal ml-1">(optionnel)</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="avenue"
                                                value={formData.avenue}
                                                onChange={handleInputChange}
                                                className={`${inputStyles} transition-all duration-200`}
                                                placeholder="Nom de la rue"
                                            />
                                        </div>

                                        {/* Quartier */}
                                        <div className="group">
                                            <label className={labelStyles}>
                                                Quartier
                                                <span className="text-gray-500 font-normal ml-1">(optionnel)</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="quartier"
                                                value={formData.quartier}
                                                onChange={handleInputChange}
                                                className={`${inputStyles} transition-all duration-200`}
                                                placeholder="Nom du quartier"
                                            />
                                        </div>
                                    </div>

                                    {/* Aperçu de l'adresse avec animation */}
                                    {(formData.avenue || formData.quartier || formData.numero || formData.city) && (
                                        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-4 mt-4 animate-fadeIn shadow-sm">
                                            <h4 className="text-amber-800 font-semibold mb-2 flex items-center">
                                                <LocationOn className="mr-2 text-amber-600" />
                                                Aperçu de l'adresse :
                                            </h4>
                                            <p className="text-gray-700 text-sm">
                                                {[
                                                    formData.numero,
                                                    formData.avenue,
                                                    formData.quartier,
                                                    formData.city,
                                                    formData.province,
                                                    formData.country
                                                ].filter(Boolean).join(', ')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Navigation Buttons améliorés */}
                            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    disabled={currentStep === 1}
                                    className="px-6 py-3 bg-gray-100 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center group shadow-sm"
                                >
                                    <ArrowBack className="mr-2 group-hover:-translate-x-1 transition-transform" />
                                    Précédent
                                </button>

                                {currentStep < 3 ? (
                                    <button
                                        type="button"
                                        onClick={nextStep}
                                        disabled={!isStepValid(currentStep)}
                                        className="px-8 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-amber-700 transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex items-center group"
                                    >
                                        <span>Continuer</span>
                                        <ArrowBack className="ml-2 rotate-180 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        disabled={!isStepValid(3) || loading}
                                        className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold shadow-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none disabled:cursor-not-allowed flex items-center group hover:shadow-xl"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                <span>Création en cours...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Business className="mr-2 group-hover:scale-110 transition-transform" />
                                                <span>Créer l'Organisation</span>
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Footer amélioré */}
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 p-4">
                            <div className="flex justify-between items-center text-gray-600 text-sm">
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center space-x-2">
                                        <span>Étape {currentStep} sur 3</span>
                                        <span className="text-gray-400">•</span>
                                        <span className={`font-medium flex items-center space-x-1 ${
                                            isStepValid(currentStep) ? 'text-green-600' : 'text-amber-600'
                                        }`}>
                                            {isStepValid(currentStep) ? (
                                                <>
                                                    <CheckCircle className="w-4 h-4" />
                                                    <span>Étape valide</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span>ⓘ</span>
                                                    <span>Champs requis manquants</span>
                                                </>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-1 bg-white/80 rounded-full px-3 py-1 border border-gray-200">
                                    {[1, 2, 3].map(step => (
                                        <div
                                            key={step}
                                            className={`w-2 h-2 rounded-full transition-all ${
                                                isStepValid(step) ? 'bg-green-500' :
                                                    currentStep >= step ? 'bg-amber-400' : 'bg-gray-300'
                                            }`}
                                        ></div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Informations supplémentaires améliorées */}
                <div className="mt-6 text-center">
                    <div className="inline-flex items-center space-x-8 text-gray-600 text-sm bg-white/90 backdrop-blur-sm rounded-2xl px-8 py-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="font-medium">Création instantanée</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span className="font-medium">Configuration flexible</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <span className="font-medium">Support 24/7</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Animation CSS */}
            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.5s ease-out;
                }
            `}</style>
        </div>
    )
}

export default OrganizationCreation